// DropdownMenu, Popover and Tooltip.

import { Check, ChevronRight } from 'lucide-react';
import {
  DropdownMenu as MenuPrimitive,
  Popover as PopoverPrimitive,
  Tooltip as TooltipPrimitive,
} from 'radix-ui';
import { createContext, useContext, type ComponentProps, type ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { InertOutside } from '../lib/inert-outside.js';

const floating =
  'z-50 rounded-md border border-border bg-popover text-fg shadow-lg data-[state=open]:animate-pop-in';

// ---------------------------------------------------------------- DropdownMenu

const MenuModal = createContext(true);

/**
 * Modal by default, like Radix: while open, an outside tap only closes the menu, and the rest
 * of the page is hidden from assistive tech and inert (see InertOutside).
 */
export function DropdownMenu({
  modal = true,
  ...props
}: ComponentProps<typeof MenuPrimitive.Root>) {
  return (
    <MenuModal.Provider value={modal}>
      <MenuPrimitive.Root modal={modal} {...props} />
    </MenuModal.Provider>
  );
}
export const DropdownMenuTrigger = MenuPrimitive.Trigger;
export const DropdownMenuGroup = MenuPrimitive.Group;
export const DropdownMenuSub = MenuPrimitive.Sub;
export const DropdownMenuRadioGroup = MenuPrimitive.RadioGroup;

export function DropdownMenuContent({
  className,
  sideOffset = 6,
  children,
  container,
  ...props
}: ComponentProps<typeof MenuPrimitive.Content> & {
  /**
   * Where the menu is rendered (default: the end of the body). A non-modal menu can render
   * inside the page's main landmark, so all of its content stays in a landmark.
   */
  container?: HTMLElement | null | undefined;
}) {
  const modal = useContext(MenuModal);
  return (
    <MenuPrimitive.Portal container={container ?? undefined}>
      <MenuPrimitive.Content
        sideOffset={sideOffset}
        className={cn(floating, 'min-w-48 p-1', className)}
        {...props}
      >
        {modal && <InertOutside />}
        {children}
      </MenuPrimitive.Content>
    </MenuPrimitive.Portal>
  );
}

const item =
  'relative flex min-h-control-sm cursor-default select-none items-center gap-2 rounded-sm px-2.5 py-1.5 text-body outline-none data-highlighted:bg-surface-hover data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-muted';

export function DropdownMenuItem({
  className,
  destructive = false,
  ...props
}: ComponentProps<typeof MenuPrimitive.Item> & { destructive?: boolean }) {
  return (
    <MenuPrimitive.Item
      className={cn(
        item,
        destructive &&
          'text-state-danger [&_svg]:text-state-danger data-highlighted:bg-state-danger-subtle',
        className,
      )}
      {...props}
    />
  );
}

export function DropdownMenuCheckboxItem({
  className,
  children,
  ...props
}: ComponentProps<typeof MenuPrimitive.CheckboxItem>) {
  return (
    <MenuPrimitive.CheckboxItem className={cn(item, 'pl-8', className)} {...props}>
      <span className="absolute left-2 flex size-4 items-center justify-center">
        <MenuPrimitive.ItemIndicator>
          <Check aria-hidden />
        </MenuPrimitive.ItemIndicator>
      </span>
      {children}
    </MenuPrimitive.CheckboxItem>
  );
}

export function DropdownMenuRadioItem({
  className,
  children,
  ...props
}: ComponentProps<typeof MenuPrimitive.RadioItem>) {
  return (
    <MenuPrimitive.RadioItem className={cn(item, 'pl-8', className)} {...props}>
      <span className="absolute left-2 flex size-4 items-center justify-center">
        <MenuPrimitive.ItemIndicator>
          <span className="size-2 rounded-full bg-primary" />
        </MenuPrimitive.ItemIndicator>
      </span>
      {children}
    </MenuPrimitive.RadioItem>
  );
}

export function DropdownMenuLabel({
  className,
  ...props
}: ComponentProps<typeof MenuPrimitive.Label>) {
  return (
    <MenuPrimitive.Label
      className={cn('px-2.5 py-1.5 text-xs font-semibold text-muted', className)}
      {...props}
    />
  );
}

export function DropdownMenuSeparator({
  className,
  ...props
}: ComponentProps<typeof MenuPrimitive.Separator>) {
  return (
    <MenuPrimitive.Separator className={cn('-mx-1 my-1 h-px bg-border', className)} {...props} />
  );
}

export function DropdownMenuShortcut({ className, ...props }: ComponentProps<'kbd'>) {
  return (
    <kbd
      className={cn('ml-auto font-mono text-xs tracking-wide text-muted', className)}
      {...props}
    />
  );
}

export function DropdownMenuSubTrigger({
  className,
  children,
  ...props
}: ComponentProps<typeof MenuPrimitive.SubTrigger>) {
  return (
    <MenuPrimitive.SubTrigger
      className={cn(item, 'data-[state=open]:bg-surface-hover', className)}
      {...props}
    >
      {children}
      <ChevronRight aria-hidden className="ml-auto" />
    </MenuPrimitive.SubTrigger>
  );
}

export function DropdownMenuSubContent({
  className,
  ...props
}: ComponentProps<typeof MenuPrimitive.SubContent>) {
  return (
    <MenuPrimitive.Portal>
      <MenuPrimitive.SubContent className={cn(floating, 'min-w-40 p-1', className)} {...props} />
    </MenuPrimitive.Portal>
  );
}

// ---------------------------------------------------------------- Popover

export const Popover = PopoverPrimitive.Root;
export const PopoverTrigger = PopoverPrimitive.Trigger;
export const PopoverAnchor = PopoverPrimitive.Anchor;

/**
 * A popover is a non-modal dialog, so it needs an accessible name (axe `aria-dialog-name`):
 * pass `aria-label`, or `aria-labelledby` pointing at a heading inside it.
 */
export type PopoverContentProps = ComponentProps<typeof PopoverPrimitive.Content> &
  ({ 'aria-label': string } | { 'aria-labelledby': string });

export function PopoverContent({
  className,
  align = 'center',
  sideOffset = 6,
  ...props
}: PopoverContentProps) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        align={align}
        sideOffset={sideOffset}
        className={cn(floating, 'w-72 p-4', className)}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
}

// ---------------------------------------------------------------- Tooltip

export const TooltipProvider = TooltipPrimitive.Provider;

export interface TooltipProps {
  content: ReactNode;
  children: ReactNode;
  side?: ComponentProps<typeof TooltipPrimitive.Content>['side'];
}

/** Simple tooltip. Needs a TooltipProvider (the app root has one). */
export function Tooltip({ content, children, side = 'top' }: TooltipProps) {
  return (
    <TooltipPrimitive.Root>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          sideOffset={6}
          className="z-50 max-w-64 rounded-md bg-surface-inverse px-2.5 py-1.5 text-xs font-medium text-inverse shadow-md data-[state=delayed-open]:animate-fade-in"
        >
          {content}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}
